import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const LOGO_IMG = "https://customer-assets.emergentagent.com/job_yarn-weight-pricer/artifacts/6sx1m0su_WhatsApp%20Image%202026-04-13%20at%207.20.45%20PM.jpeg";

export default function PinLoginPage() {
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  const submitPin = async (pin) => {
    setLoading(true);
    try {
      await api.post("/auth/pin-login", { pin });
      toast.success("Access granted");
      navigate("/view/calculator");
    } catch (err) {
      toast.error(formatApiError(err));
      setDigits(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, value) => {
    const v = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = v;
    setDigits(next);

    if (v && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits entered
    if (v && index === 5) {
      const pin = next.join("");
      if (pin.length === 6) submitPin(pin);
    } else if (v) {
      // Check if all filled after setting
      const pin = next.join("");
      if (pin.length === 6 && !next.includes("")) submitPin(pin);
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    if (pasted.length === 6) submitPin(pasted);
    else inputRefs.current[pasted.length]?.focus();
  };

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="pin-login-page">
      <div className="w-full max-w-xs space-y-8 text-center">
        <div className="space-y-4">
          <img src={LOGO_IMG} alt="Logo" className="h-20 mx-auto" />
          <h2 className="font-heading text-xl font-semibold text-foreground">Dhoti Costing Calculator</h2>
          <p className="text-sm text-muted-foreground">Please Enter 6 Digit PIN</p>
        </div>

        <div className="flex justify-center gap-3" data-testid="pin-dots" onPaste={handlePaste}>
          {digits.map((d, i) => (
            <div key={i} className="relative">
              <input
                ref={(el) => (inputRefs.current[i] = el)}
                type="password"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                data-testid={`pin-digit-${i}`}
                className="w-12 h-12 text-center text-transparent caret-transparent border-0 bg-transparent outline-none absolute inset-0 z-10"
                disabled={loading}
              />
              <div
                className={`w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
                  d
                    ? "bg-[#C2410C] border-[#C2410C] scale-100"
                    : "bg-white border-stone-300 scale-100"
                }`}
              >
                {d && (
                  <div className="w-3 h-3 rounded-full bg-white" />
                )}
              </div>
            </div>
          ))}
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin text-[#C2410C]" />
            Verifying...
          </div>
        )}
      </div>
    </div>
  );
}
